import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { authRoutes } from "@/modules/auth/router";
import { landingRoutes } from "@/modules/landing/router";
import { dashboardRoutes } from "@/modules/dashboard/router";

const routes: Array<RouteRecordRaw> = [
  ...landingRoutes,
  ...authRoutes,
  ...dashboardRoutes,
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

import { useSnackbar } from "@/shared/composables/useSnackbar";

function getTokenPayload(token: string | null): any {
  if (!token) return null;
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

function isTokenValid(token: string | null): boolean {
  const payload = getTokenPayload(token);
  if (!payload) return false;
  if (payload.exp) {
    // Consider expired if it expires within the next 5 seconds
    return Date.now() / 1000 < payload.exp - 5;
  }
  return true;
}

router.beforeEach((to, from, next) => {
  const defaultTitle = "Check-It Now";
  const title = to.meta.title;
  document.title = title ? `${title} | ${defaultTitle}` : defaultTitle;

  const rawToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");

  const isAccessValid = isTokenValid(rawToken);
  // La sesión es válida si el access_token no ha expirado,
  // o si tenemos un refresh_token (el interceptor renovará el access_token en el primer request)
  const isAuthenticated = isAccessValid || !!refreshToken;

  // Si la sesión expiró por completo (no hay token válido ni refresh token), limpiar storage
  if (!isAuthenticated && (rawToken || refreshToken)) {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    const { showSnackbar } = useSnackbar();
    // Only show snackbar if they are trying to access a protected route
    if (to.meta.requiresAuth) {
      showSnackbar({
        title: "Sesión Expirada",
        message:
          "Tu sesión ha expirado por completo. Por favor, inicia sesión nuevamente.",
        type: "warning",
      });
    }
  }

  const payload = getTokenPayload(rawToken);
  let needsCompanySetup = false;
  if (payload) {
    const roles = payload.roles || [];
    const hasCompany = !!payload.companyId;
    const skipCompanySetup =
      sessionStorage.getItem("skipCompanySetup") === "true";
    if (roles.includes("ADMIN") && !hasCompany && !skipCompanySetup) {
      needsCompanySetup = true;
    }
  }

  const isCompanySetupRoute =
    to.path === "/register" && to.query.step === "company-setup";

  if (to.meta.requiresAuth && !isAuthenticated) {
    // Intenta ir a zona privada sin login
    next("/login");
  } else if (isAuthenticated && needsCompanySetup && !isCompanySetupRoute) {
    // Forzamos la configuración de la empresa si es admin y no tiene una
    next("/register?step=company-setup");
  } else if (to.meta.guestOnly && isAuthenticated) {
    // Intenta ir a login/registro estando ya logueado
    if (isCompanySetupRoute) {
      next(); // Permitimos que configuren la empresa aunque ya estén logueados
    } else {
      next("/dashboard");
    }
  } else {
    next();
  }
});

export default router;
